import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Observable, Subject, filter, takeUntil } from 'rxjs';

interface ProgressState {
  currentStep: number;
  totalSteps: number;
  isVisible: boolean;
  // Invoice orders are paid after ordering, so the payment step describes the invoice.
  invoice: boolean;
}

const INVOICE_PAYMENT_STEP = { label: 'Faktura', subtitle: 'Betales før kortene sendes' };

@Injectable({
  providedIn: 'root'
})
export class OrderProgressService implements OnDestroy {

  private destroy$ = new Subject<void>();

  private readonly STEPS = [
    { id: 'customize', label: 'Velg', subtitle: 'Beløp og antall', icon: 'gift-outline' },
    { id: 'payment', label: 'Betal', subtitle: 'Vipps eller kort', icon: 'card-outline' },
    { id: 'distribution', label: 'Send', subtitle: 'Design og mottakere', icon: 'create-outline' }
  ];

  private state$ = new BehaviorSubject<ProgressState>({
    currentStep: -1,
    totalSteps: this.STEPS.length,
    isVisible: false,
    invoice: false
  });

  constructor(private router: Router) {
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((e: NavigationEnd) => {
      this.updateProgressFromUrl(e.url);
    });

    // Handle initial route - increased timeout and added fallback
    setTimeout(() => {
      this.updateProgressFromUrl(this.router.url);
    }, 100);
  }

  private updateProgressFromUrl(url: string): void {
    // Hide on receipt only
    if (url.includes('receipt')) {
      this.hide();
      return;
    }

    // Check specific routes and animate to them
    if (url.includes('distribution')) {
      this.show();
      // On distribution page, Design & Send (step 2) should be ACTIVE, not completed
      // We have 3 steps: 0=Velg, 1=Betal, 2=Design & Send
      // Setting currentStep to 2 means:
      // - Steps 0 and 1 are completed
      // - Step 2 is ACTIVE (not completed)
      this.setStep(2); // Step 2 active, NOT completed
    } else if (url.includes('payment')) {
      this.show();
      // On payment page, Payment should be ACTIVE
      this.setStep(1); // Payment step is active
    } else if (url.includes('customize-card') || url === '/' || url === '') {
      // Customize card page - first step
      this.setStep(0);
      this.show();
    } else {
      // Any other route (like store slugs) is also customize step
      this.setStep(0);
      this.show();
    }
  }

  setStep(index: number): void {
    // Step 3 means all steps are completed
    // Step 2 is the last actual step (Design & Send)
    // But we use step 3 to indicate everything is done
    this.state$.next({
      ...this.state$.value,
      currentStep: index
    });
  }

  show(): void {
    this.state$.next({ ...this.state$.value, isVisible: true });
  }

  hide(): void {
    this.state$.next({ ...this.state$.value, isVisible: false });
  }

  setInvoiceMode(invoice: boolean): void {
    if (this.state$.value.invoice === invoice) return;
    this.state$.next({ ...this.state$.value, invoice });
  }

  getState(): Observable<ProgressState> {
    return this.state$.asObservable();
  }

  getSteps() {
    const { currentStep, invoice } = this.state$.value;

    const steps = this.STEPS.map((baseStep, index) => {
      const step = invoice && baseStep.id === 'payment' ? { ...baseStep, ...INVOICE_PAYMENT_STEP } : baseStep;

      // Special case: currentStep 3 means everything is completed
      if (currentStep >= this.STEPS.length) {
        return {
          ...step,
          isActive: false,
          isCompleted: true,
          isPending: false
        };
      }

      // Normal progression
      const isActive = index === currentStep;
      const isCompleted = index < currentStep;
      const isPending = index > currentStep;

      return {
        ...step,
        isActive,
        isCompleted,
        isPending
      };
    });

    return steps;
  }

  /**
   * Animate from one step to another with a delay
   * Useful for showing payment completion before moving to distribution
   */
  animateToStep(targetStep: number, delayMs: number = 400): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        this.setStep(targetStep);
        resolve();
      }, delayMs);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
